/**
 * ProductSelectorTable
 * Grouped expandable lot table for PI product selection.
 * Matches Transfer table UI: separate Cases / Units inputs per lot, budget-aware.
 */
import React, { useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Typography, Box, IconButton, Collapse,
  InputAdornment, useTheme, alpha, Chip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

export default function ProductSelectorTable({
  groupedLots,
  committedCases,       // { inventoryItemId: totalBaseUnits }
  lotSelections,        // { lotId: { cases: N, units: M } }
  onSelectionChange,    // (lotId, 'cases'|'units', value) => void
  existingInventoryItemIds, // Set<string>
  itemSearchTerm
}) {
  const theme = useTheme();
  const [expandedItems, setExpandedItems] = useState({});
  const [lotSearchTerms, setLotSearchTerms] = useState({});

  const filteredGroups = useMemo(() => {
    if (!itemSearchTerm) return groupedLots;
    const lower = itemSearchTerm.toLowerCase();
    return groupedLots.filter(g => g.displayName.toLowerCase().includes(lower));
  }, [groupedLots, itemSearchTerm]);

  const toggleExpand = (key) => {
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getFilteredLots = (key, lots) => {
    const term = (lotSearchTerms[key] || '').toLowerCase();
    if (!term) return lots;
    return lots.filter(
      lot =>
        (lot.lot_number || '').toLowerCase().includes(term) ||
        (lot.batch_number || '').toLowerCase().includes(term)
    );
  };

  const formatDate = (ds) => {
    if (!ds) return '—';
    const [y, m, d] = ds.split('-').map(Number);
    if (isNaN(y)) return '—';
    return new Date(y, m - 1, d).toLocaleDateString();
  };

  return (
    <Box>
      <TableContainer component={Paper} sx={{ maxHeight: 500, border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, borderRadius: 2 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48, bgcolor: 'white' }} />
              <TableCell sx={{ fontWeight: 500, bgcolor: 'white' }}>Product</TableCell>
              <TableCell align="right" sx={{ fontWeight: 500, bgcolor: 'white' }}>Total Cases</TableCell>
              <TableCell align="right" sx={{ fontWeight: 500, bgcolor: 'white' }}>Available</TableCell>
              <TableCell align="right" sx={{ fontWeight: 500, bgcolor: 'white' }}>Selected</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredGroups.map(({ key, displayName, lots }) => {
              const isExpanded = expandedItems[key];
              const filteredLots = getFilteredLots(key, lots);

              const totalCasesGroup = lots.reduce((sum, lot) => {
                const upc = lot.units_per_case || 1;
                return sum + Math.floor((lot.total_base_units || 0) / upc);
              }, 0);
              const totalCommittedCases = lots.reduce((sum, lot) => {
                const upc = lot.units_per_case || 1;
                return sum + Math.floor((committedCases[lot.id] || 0) / upc);
              }, 0);
              const totalFree = Math.max(0, totalCasesGroup - totalCommittedCases);

              // Total selected in base units across all lots in this group
              const totalSelectedBaseUnits = lots.reduce((sum, lot) => {
                const upc = lot.units_per_case || 1;
                const sel = lotSelections[lot.id] || {};
                return sum + (sel.cases || 0) * upc + (sel.units || 0);
              }, 0);
              const hasSelected = totalSelectedBaseUnits > 0;

              return (
                <React.Fragment key={key}>
                  {/* Group header row */}
                  <TableRow
                    hover
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isExpanded ? alpha(theme.palette.primary.main, 0.04) : 'inherit'
                    }}
                    onClick={() => toggleExpand(key)}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {displayName}
                      {hasSelected && (
                        <Chip
                          label={`${totalSelectedBaseUnits} units`}
                          size="small"
                          color="primary"
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {totalCasesGroup.toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      {totalFree.toLocaleString()} cases
                      {totalCommittedCases > 0 && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                          {totalCommittedCases} in other orders
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {hasSelected ? (
                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                          {totalSelectedBaseUnits} units
                        </Typography>
                      ) : '—'}
                    </TableCell>
                  </TableRow>

                  {/* Expanded lot detail rows */}
                  <TableRow>
                    <TableCell colSpan={5} sx={{ p: 0, border: 'none' }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            p: 2.5,
                            bgcolor: alpha(theme.palette.grey[50], 0.6),
                            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
                          }}
                        >
                          {/* Lot search */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main', whiteSpace: 'nowrap' }}>
                              Lots ({filteredLots.length}
                              {lotSearchTerms[key] ? ` of ${lots.length}` : ''})
                            </Typography>
                            <TextField
                              placeholder="Search lot/batch..."
                              size="small"
                              value={lotSearchTerms[key] || ''}
                              onChange={(e) =>
                                setLotSearchTerms(prev => ({ ...prev, [key]: e.target.value }))
                              }
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                                  </InputAdornment>
                                )
                              }}
                              sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white', fontSize: '0.85rem' }
                              }}
                            />
                          </Box>

                          {/* Lot table */}
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 500, bgcolor: 'white' }}>Lot #</TableCell>
                                <TableCell sx={{ fontWeight: 500, bgcolor: 'white' }}>Batch #</TableCell>
                                <TableCell sx={{ fontWeight: 500, bgcolor: 'white' }}>Expiry</TableCell>
                                <TableCell sx={{ fontWeight: 500, bgcolor: 'white' }}>Units/Case</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 500, bgcolor: 'white' }}>In Other Orders</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 500, bgcolor: 'white' }}>Available</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 500, bgcolor: 'white' }}>Cases</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 500, bgcolor: 'white' }}>Units</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredLots.map(lot => {
                                const upc = lot.units_per_case || 1;
                                const committedBaseUnits = committedCases[lot.id] || 0;
                                const inOtherOrdersCases = Math.floor(committedBaseUnits / upc);
                                const inOtherOrdersUnits = committedBaseUnits; // base units
                                const lotTotalCases = Math.floor((lot.total_base_units || 0) / upc);
                                const lotTotalUnits = lot.total_base_units || 0;
                                const freeCases = Math.max(0, lotTotalCases - inOtherOrdersCases);
                                const freeUnits = Math.max(0, lotTotalUnits - inOtherOrdersUnits);

                                const sel = lotSelections[lot.id] || {};
                                const entCases = sel.cases || 0;
                                const entUnits = sel.units || 0;

                                // Budget-aware maxes: cases and units share the same pool
                                const maxCases = Math.max(0, Math.min(freeCases, Math.floor(Math.max(0, freeUnits - entUnits) / upc)));
                                const maxUnits = Math.max(0, freeUnits - entCases * upc);

                                const baseUnitsSelected = entCases * upc + entUnits;
                                const alreadyInOrder = existingInventoryItemIds.has(lot.id);

                                return (
                                  <TableRow
                                    key={lot.id}
                                    sx={{
                                      opacity: alreadyInOrder ? 0.55 : 1,
                                      bgcolor: alreadyInOrder
                                        ? alpha(theme.palette.success.main, 0.04)
                                        : 'inherit'
                                    }}
                                  >
                                    <TableCell>{lot.lot_number || '—'}</TableCell>
                                    <TableCell>{lot.batch_number || '—'}</TableCell>
                                    <TableCell>{formatDate(lot.expiry_date)}</TableCell>
                                    <TableCell>
                                      <Typography variant="caption" color="text.secondary">
                                        {upc} units/case
                                      </Typography>
                                    </TableCell>

                                    {/* In Other Orders */}
                                    <TableCell align="right">
                                      {inOtherOrdersCases > 0 || inOtherOrdersUnits > 0 ? (
                                        <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500 }}>
                                          {upc > 1 ? (
                                            <>
                                              {inOtherOrdersCases > 0 && `${inOtherOrdersCases} cases`}
                                              {inOtherOrdersCases > 0 && inOtherOrdersUnits % upc > 0 && ' · '}
                                              {inOtherOrdersUnits % upc > 0 && `${inOtherOrdersUnits % upc} units`}
                                            </>
                                          ) : (
                                            `${inOtherOrdersUnits} units`
                                          )}
                                        </Typography>
                                      ) : (
                                        <Typography variant="body2" color="text.disabled">—</Typography>
                                      )}
                                    </TableCell>

                                    {/* Available */}
                                    <TableCell align="right">
                                      {upc > 1 ? (
                                        <Box sx={{ textAlign: 'right' }}>
                                          <Typography variant="body2" sx={{ fontWeight: 500, color: freeCases === 0 ? 'error.main' : 'success.main' }}>
                                            {freeCases.toLocaleString()} cases
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {freeUnits.toLocaleString()} units total
                                          </Typography>
                                        </Box>
                                      ) : (
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: freeUnits === 0 ? 'error.main' : 'success.main' }}>
                                          {freeUnits.toLocaleString()} units
                                        </Typography>
                                      )}
                                    </TableCell>

                                    {/* Cases input */}
                                    <TableCell align="center">
                                      {alreadyInOrder ? (
                                        <Chip
                                          label="Already in Order"
                                          size="small"
                                          color="success"
                                          icon={<CheckCircleIcon />}
                                          sx={{ fontSize: '0.7rem', height: 22 }}
                                        />
                                      ) : upc > 1 ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                          <TextField
                                            type="number"
                                            size="small"
                                            value={entCases || ''}
                                            onChange={(e) => {
                                              const raw = e.target.value;
                                              if (raw === '') { onSelectionChange(lot.id, 'cases', 0); return; }
                                              const val = parseInt(raw, 10);
                                              if (isNaN(val) || val < 0) { onSelectionChange(lot.id, 'cases', 0); return; }
                                              onSelectionChange(lot.id, 'cases', Math.min(val, maxCases));
                                            }}
                                            inputProps={{ min: 0, max: maxCases }}
                                            placeholder="0"
                                            label={`/ ${maxCases}`}
                                            disabled={freeCases === 0}
                                            sx={{
                                              width: 90,
                                              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                                              '& input[type=number]': { MozAppearance: 'textfield' }
                                            }}
                                          />
                                          {baseUnitsSelected > 0 && (
                                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500, fontSize: '0.68rem' }}>
                                              = {baseUnitsSelected} units
                                            </Typography>
                                          )}
                                        </Box>
                                      ) : (
                                        <Typography variant="body2" color="text.disabled">—</Typography>
                                      )}
                                    </TableCell>

                                    {/* Units input */}
                                    <TableCell align="center">
                                      {alreadyInOrder ? null : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                          <TextField
                                            type="number"
                                            size="small"
                                            value={entUnits || ''}
                                            onChange={(e) => {
                                              const raw = e.target.value;
                                              if (raw === '') { onSelectionChange(lot.id, 'units', 0); return; }
                                              const val = parseInt(raw, 10);
                                              if (isNaN(val) || val < 0) { onSelectionChange(lot.id, 'units', 0); return; }
                                              onSelectionChange(lot.id, 'units', Math.min(val, maxUnits));
                                            }}
                                            inputProps={{ min: 0, max: maxUnits }}
                                            placeholder="0"
                                            label={`/ ${maxUnits}`}
                                            disabled={freeUnits === 0}
                                            sx={{
                                              width: 90,
                                              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { display: 'none' },
                                              '& input[type=number]': { MozAppearance: 'textfield' }
                                            }}
                                          />
                                          {upc > 1 && entUnits > 0 && (
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                                              extra units
                                            </Typography>
                                          )}
                                        </Box>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                              {filteredLots.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={8} align="center" sx={{ py: 2.5, color: 'text.secondary' }}>
                                    <Typography variant="body2">
                                      {lotSearchTerms[key] ? 'No matching lots found' : 'No lots available'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}

            {filteredGroups.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {itemSearchTerm
                      ? 'No products match your search'
                      : 'No inventory available at this location'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
